/**
 * Task #249 — Platform staff role bundles.
 *
 * Single shared source of truth mapping each platform role to the set of
 * platform modules it can reach. This mirrors the *concept* of the tenant
 * roles/permissions system but is intentionally simpler: each role is a
 * fixed bundle of module keys. There are NO per-person overrides in v1.
 *
 * Used on the server (deny-by-default request gating in
 * `server/platform/auth.ts`) and on the client (nav filtering + page
 * guards in `client/src/pages/platform/platform-layout.tsx`).
 */
import type { PlatformUserRole } from "../schema";

// Module keys map to the platform left-nav / top-toolbar surfaces.
// `administration` covers the whole Super Admin group (platform settings,
// user management, Gmail Read, AI Agents/Help/Assistants, Imports, PII
// scan). It stays super_admin-only in v1.
export const PLATFORM_MODULE_KEYS = [
  "dashboard",
  "prospects",
  "customers",
  "invoices",
  "billing",
  "sending_infra",
  "profiles",
  "communications",
  "action_history",
  "lead_gen",
  "feedback",
  "administration",
] as const;

export type PlatformModuleKey = (typeof PLATFORM_MODULE_KEYS)[number];

const ALL_NON_ADMIN_MODULES: PlatformModuleKey[] = [
  "dashboard",
  "prospects",
  "customers",
  "invoices",
  "billing",
  "sending_infra",
  "profiles",
  "communications",
  "action_history",
  "lead_gen",
  "feedback",
];

/**
 * Role → allowed module keys. Bundles defined once here; super admins
 * assign roles, they do not edit bundles per person (v1).
 */
export const PLATFORM_ROLE_BUNDLES: Record<PlatformUserRole, PlatformModuleKey[]> = {
  // Full access incl. administration. Only role that can manage platform users.
  super_admin: [...PLATFORM_MODULE_KEYS],
  // Everything except administration.
  admin: [...ALL_NON_ADMIN_MODULES],
  // Oversight/read across the operational modules. No administration.
  manager: [
    "dashboard",
    "prospects",
    "customers",
    "invoices",
    "billing",
    "communications",
    "action_history",
    "lead_gen",
  ],
  // Provisional until ticketing exists. Cross-tenant read + impersonation
  // (impersonation handled separately by requireImpersonationRole).
  support: ["dashboard", "customers", "communications", "action_history"],
  // Subscriptions / invoices / billing + customers (read).
  billing: ["dashboard", "invoices", "billing", "customers"],
  // Outbound sales surfaces.
  sales: ["dashboard", "communications", "lead_gen", "prospects", "customers"],
  // Sending infrastructure + beta feedback triage.
  developer: ["dashboard", "sending_infra", "feedback"],
  // Read-only across operational modules (writes blocked, see
  // READ_ONLY_PLATFORM_ROLES). No impersonation, no administration.
  auditor: [
    "dashboard",
    "prospects",
    "customers",
    "invoices",
    "billing",
    "communications",
    "action_history",
    "lead_gen",
  ],
  // Read across action history + communications.
  compliance: ["dashboard", "action_history", "communications"],
  // Legacy — minimal bundle so historical rows behave sanely.
  marketing: ["dashboard", "communications"],
};

/**
 * Roles whose access is read-only: the backend rejects any mutating
 * request (POST/PUT/PATCH/DELETE) even within an allowed module.
 */
export const READ_ONLY_PLATFORM_ROLES = new Set<PlatformUserRole>([
  "auditor",
  "compliance",
]);

export function isReadOnlyPlatformRole(role: PlatformUserRole): boolean {
  return READ_ONLY_PLATFORM_ROLES.has(role);
}

export function canRoleAccessModule(
  role: PlatformUserRole,
  moduleKey: PlatformModuleKey,
): boolean {
  const bundle = PLATFORM_ROLE_BUNDLES[role];
  if (!bundle) return false;
  return bundle.includes(moduleKey);
}

// ── Role metadata for the management UI ──────────────────────────────────────

/**
 * Roles offered when creating/editing platform staff, in display order.
 * `marketing` is intentionally excluded (legacy-only).
 */
export const ALL_PLATFORM_ROLES: PlatformUserRole[] = [
  "super_admin",
  "admin",
  "manager",
  "support",
  "billing",
  "sales",
  "developer",
  "auditor",
  "compliance",
];

export const PLATFORM_ROLE_LABELS: Record<PlatformUserRole, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  manager: "Manager",
  support: "Support",
  billing: "Billing",
  sales: "Sales",
  developer: "Developer",
  auditor: "Auditor (read-only)",
  compliance: "Compliance",
  marketing: "Marketing (legacy)",
};

export function isValidPlatformRole(role: string): role is PlatformUserRole {
  return Object.prototype.hasOwnProperty.call(PLATFORM_ROLE_BUNDLES, role);
}

// ── Client nav mapping ───────────────────────────────────────────────────────

/**
 * Maps a platform nav/toolbar href to the module key that gates it.
 * Returns null for paths that should always be visible (e.g. login).
 */
export function navHrefToModule(href: string): PlatformModuleKey | null {
  // Order matters: most specific first.
  const RULES: Array<[string, PlatformModuleKey]> = [
    ["/platform/dashboard", "dashboard"],
    ["/platform/prospects", "prospects"],
    ["/platform/customers", "customers"],
    ["/platform/invoices", "invoices"],
    ["/platform/billing", "billing"],
    ["/platform/infra", "sending_infra"],
    ["/platform/profiles", "profiles"],
    ["/platform/communications", "communications"],
    ["/platform/email", "communications"],
    ["/platform/approvals", "action_history"],
    ["/platform/lead-gen", "lead_gen"],
    ["/platform/feedback", "feedback"],
    // Administration group (super_admin only in v1).
    ["/platform/admin", "administration"],
    ["/platform/gmail-read", "administration"],
    ["/platform/ai-agents", "administration"],
    ["/platform/ai-intelligence", "administration"],
    ["/platform/ai-assistants", "administration"],
    ["/platform/ai-import", "administration"],
  ];
  for (const [prefix, moduleKey] of RULES) {
    if (href === prefix || href.startsWith(prefix + "/")) return moduleKey;
  }
  return null;
}

// ── Server request mapping ───────────────────────────────────────────────────

/**
 * Maps an /api/platform/* request path to the module key that gates it.
 * Unmapped paths fall through to `administration` (super_admin only) so
 * enforcement is deny-by-default: a role only reaches an endpoint that is
 * explicitly mapped into one of its bundle modules.
 *
 * `path` should be the full request path (e.g. req.baseUrl + req.path).
 */
export function resolvePlatformApiModule(path: string): PlatformModuleKey {
  const RULES: Array<[string, PlatformModuleKey]> = [
    // Dashboard widgets (audit-log/health/ip-check power the dashboard).
    ["/api/platform/dashboard", "dashboard"],
    ["/api/platform/health", "dashboard"],
    ["/api/platform/ip-check", "dashboard"],
    ["/api/platform/ipinfo-usage", "dashboard"],
    ["/api/platform/audit-log", "dashboard"],
    ["/api/platform/prospects", "prospects"],
    ["/api/platform/customers", "customers"],
    ["/api/platform/invoices", "invoices"],
    ["/api/platform/billing", "billing"],
    ["/api/platform/infra", "sending_infra"],
    ["/api/platform/users", "profiles"],
    ["/api/platform/communications", "communications"],
    ["/api/platform/beta", "action_history"],
    ["/api/platform/lead-gen", "lead_gen"],
    ["/api/platform/feedback", "feedback"],
  ];
  for (const [prefix, moduleKey] of RULES) {
    if (path === prefix || path.startsWith(prefix + "/")) return moduleKey;
  }
  return "administration";
}
